import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { clientLogger } from "@/lib/logger-client";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Opens a URL in a new tab and detects if the popup was blocked
 * @param url The URL to open
 * @returns An object with success status and the window reference if successful
 */
export function openUrlInNewTab(url: string): {
  success: boolean;
  window: Window | null;
} {
  try {
    // Attempt to open the window
    const newWindow = window.open(url, "_blank");

    // Check if the window was successfully opened
    // If newWindow is null or undefined, the popup was likely blocked
    const success = newWindow !== null && typeof newWindow !== "undefined";

    // Additional check: try to access a property of the window
    // This will throw an error if the popup was blocked
    if (success && newWindow) {
      try {
        // Try to access a property of the window
        // This will throw an error if the popup was blocked
        const test = newWindow.location.href;

        // If we can access the location, the popup was not blocked
        // But if it's about:blank, it might still be blocked in some browsers
        if (test === "about:blank") {
          // Try to change the location to the URL
          newWindow.location.href = url;
        }

        // Log success for debugging
        console.log("Popup opened successfully");
        return { success: true, window: newWindow };
      } catch (e) {
        // If we can't access the location, the popup was blocked
        console.log("Popup blocked (access error):", e);
        return { success: false, window: null };
      }
    }

    // If newWindow is null or undefined, the popup was blocked
    if (!success) {
      console.log("Popup blocked (null window)");
    }

    return {
      success,
      window: newWindow,
    };
  } catch (error) {
    // If there's an error, the popup was likely blocked
    clientLogger.error("Error opening popup:", { error });
    return { success: false, window: null };
  }
}

/**
 * Shows an alert dialog with instructions on how to enable popups
 */
export function showPopupBlockedAlert() {
  // Remove any existing alerts first
  const existingAlerts = document.querySelectorAll(".popup-blocked-alert");
  existingAlerts.forEach((alert) => alert.remove());

  // Create container div that will be centered vertically and horizontally
  const container = document.createElement("div");
  container.className =
    "fixed inset-0 z-50 flex items-center justify-center popup-blocked-alert";

  // Create semi-transparent background that only covers the alert area
  const background = document.createElement("div");
  background.className = "absolute inset-0 bg-black bg-opacity-50";

  // Create alert element
  const alertDiv = document.createElement("div");
  alertDiv.className =
    "relative bg-destructive-soft border-2 border-destructive shadow-lg rounded-md p-4 max-w-md";
  alertDiv.style.maxWidth = "90vw";

  // Add content
  alertDiv.innerHTML = `
    <div class="flex items-start">
      <div class="shrink-0 text-destructive">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-8 w-8">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      </div>
      <div class="ml-3">
        <h3 class="text-base font-bold text-destructive">Action Required: Popup Blocked</h3>
        <div class="mt-2 text-sm text-foreground">
          <p class="font-medium">Your browser has blocked the popup window. To view your document:</p>
          <ol class="list-decimal pl-5 mt-2 space-y-1">
            <li>Look for the popup icon <span class="font-semibold">in your browser's address bar</span></li>
            <li>Click on it and select "<span class="font-semibold">Always allow popups from this site</span>"</li>
            <li>Then click the "Generate" button again</li>
          </ol>
        </div>
        <div class="mt-4 flex justify-end">
          <button type="button" id="close-popup-alert" class="px-4 py-2 bg-surface text-surface-foreground border border-border rounded hover:bg-muted text-sm font-medium">
            Dismiss
          </button>
        </div>
      </div>
    </div>
  `;

  // Add elements to DOM
  container.appendChild(background);
  container.appendChild(alertDiv);
  document.body.appendChild(container);

  // Function to remove the alert
  const removeAlert = () => {
    if (document.body.contains(container)) {
      container.remove();
    }
  };

  // Add event listener to close button
  const closeButton = document.getElementById("close-popup-alert");
  if (closeButton) {
    closeButton.addEventListener("click", removeAlert);
  }

  // Add event listener to background (close when clicking outside the alert)
  background.addEventListener("click", removeAlert);

  // Auto-remove after 30 seconds
  setTimeout(removeAlert, 30000);

  // Log for debugging
  console.log("Popup blocked alert shown");
}
